import { getLastTranslationProvider, setTranslationProvider, translatePosts } from './translation'

const BLUE_TRACKER_RSS_URL = 'https://www.wowhead.com/blue-tracker?rss'
const NOOB_CLUB_NEWS_URL = 'https://www.noob-club.ru/'
const WOWHEAD_NEWS_FEEDS = [
  {
    key: 'all',
    label: 'All News',
    shortLabel: 'All',
    url: 'https://www.wowhead.com/news/rss/retail',
    kind: 'aggregate',
    sources: ['retail', 'classic', 'blue'],
  },
  {
    key: 'blue',
    label: 'Blue Tracker',
    shortLabel: 'Blue',
    url: BLUE_TRACKER_RSS_URL,
    kind: 'blue',
  },
  {
    key: 'retail',
    label: 'Retail WoW',
    shortLabel: 'Retail',
    url: 'https://www.wowhead.com/news/rss/retail',
    kind: 'news',
  },
  {
    key: 'classic',
    label: 'Classic WoW',
    shortLabel: 'Classic',
    url: 'https://www.wowhead.com/news/rss/classic',
    kind: 'news',
  },
  {
    key: 'noob-club',
    label: 'Noob Club',
    shortLabel: 'Noob Club',
    url: NOOB_CLUB_NEWS_URL,
    kind: 'noob',
    source: 'noob-club',
    origin: 'https://www.noob-club.ru',
    nativeLanguage: 'ru',
  },
]
const WOWHEAD_WORKER_URL = 'https://wowhead-rss-proxy.andrey-ilutsa.workers.dev'
const WOWHEAD_ARTICLE_PARSER_VERSION = '2026-07-28-noob-club-site-v23'
const FEED_CACHE_TTL_MS = 5 * 60 * 1000
const FEED_STORAGE_PREFIX = 'rio-news-feed-cache-v1:'
const feedCache = new Map()
const feedRequests = new Map()
const feedFallbacks = new Map()

function createWorkerUrl(path, params = {}) {
  const workerUrl = new URL(path, WOWHEAD_WORKER_URL)
  Object.entries(params).forEach(([key, value]) => {
    if (value) workerUrl.searchParams.set(key, value)
  })
  return workerUrl.toString()
}

function createProxyUrl(url) {
  return createWorkerUrl('/', { url })
}

function decodeHtml(value = '') {
  const textarea = document.createElement('textarea')
  textarea.innerHTML = value
  return textarea.value
}

function stripHtml(value = '') {
  const container = document.createElement('div')
  container.innerHTML = value
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|ul|ol|section|article|blockquote|h[1-6])>/gi, ' ')
  return container.textContent || container.innerText || ''
}

function cleanFeedSummary(value = '') {
  return String(value || '')
    .replace(/\s*Continue reading\s*»?\s*$/i, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function normalizedPostTextKey(value = '') {
  return cleanFeedSummary(value)
    .replace(/\s+By[A-Za-z0-9 ._-]+ on \d{4}\/\d{2}\/\d{2} at \d{1,2}:\d{2}\s*(?:AM|PM)?\s*$/i, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

const ENCODED_ARTICLE_TAG_PATTERN = /&lt;\/?(?:a|b|blockquote|br|code|div|em|h[1-6]|i|li|ol|p|span|strong|table|tbody|td|th|thead|tr|ul)\b/i

function cleanFeedSummaryText(value = '') {
  return String(value || '')
    .replace(/\s*Continue reading\s*(?:\u00bb|\u0412\u00bb|&raquo;)?\s*$/i, '')
    .replace(/\s*(?:\.{3}|\u2026)?\s*By\s*.+?\s+on\s+\d{4}\/\d{2}\/\d{2}\s+at\s+\d{1,2}:\d{2}\s*(?:AM|PM)?\s*$/i, '')
    .replace(/\[\/?(?:b|i|u|s|center|left|right|quote|spoiler|fancy)(?:=[^\]]*)?\]/gi, ' ')
    .replace(/\[url=[^\]]+\]([\s\S]*?)\[\/url\]/gi, '$1')
    .replace(/\[img\][\s\S]*?\[\/img\]/gi, ' ')
    .replace(/"?quotquot"?/gi, '"')
    .replace(/\s+/g, ' ')
    .trim()
}

function normalizedFeedTextKey(value = '') {
  return cleanFeedSummaryText(value)
    .replace(/\s*(?:\.{3}|\u2026)?\s*By\s*.+?\s+on\s+\d{4}\/\d{2}\/\d{2}\s+at\s+\d{1,2}:\d{2}\s*(?:AM|PM)?\s*$/i, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

function bluePostDedupeKeys(post) {
  if (post.kind !== 'blue') return []

  const titleKey = normalizedFeedTextKey(post.title)
    .replace(/\b(?:january|february|march|april|may|june|july|august|september|october|november|december)\b/g, '')
    .replace(/\b\d{1,4}\b/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  const summaryKey = normalizedFeedTextKey(post.summary)
  const keys = []

  if (titleKey.length > 18) keys.push(`blue-title:${titleKey}`)
  if (summaryKey.length > 40) keys.push(`blue-summary:${summaryKey}`)

  return keys
}

function decodeEscapedArticleHtml(value = '') {
  let html = String(value || '')

  for (let attempts = 0; attempts < 5 && ENCODED_ARTICLE_TAG_PATTERN.test(html); attempts += 1) {
    html = decodeHtml(html)
  }

  return html
}

function normalizeArticleBlock(block = {}) {
  if (!block || typeof block !== 'object') return block

  if (['html', 'quote', 'bluepost'].includes(block.type)) {
    return {
      ...block,
      html: decodeEscapedArticleHtml(block.html || block.text || ''),
    }
  }

  if (block.type === 'list') {
    return {
      ...block,
      items: (block.items || []).map((item) => decodeEscapedArticleHtml(item)),
    }
  }

  if (['paragraph', 'heading'].includes(block.type)) {
    const decoded = decodeEscapedArticleHtml(block.text || '')
    return {
      ...block,
      text: /<\/?[a-z][^>]*>/i.test(decoded) ? stripHtml(decoded).replace(/\s+/g, ' ').trim() : decoded,
    }
  }

  return block
}

function isNoobClubPageChrome(block = {}) {
  const content = block.type === 'list'
    ? (block.items || []).join(' ')
    : block.html || block.text || ''

  const decoded = decodeEscapedArticleHtml(content)

  return /(?:action=profile|profile;u=|id=["']?top_subject)/i.test(decoded)
    || (block.type === 'list' && /action=forum|board=\d/i.test(content))
    || /^(?:SMF\s+\d|TinyPortal\b)/i.test(stripHtml(decoded).trim())
}

function normalizeNoobClubArticleBlock(block = {}) {
  if (block.type === 'image') {
    if (isNoobClubDecorativeImageUrl(block.src)) return null
    return { ...block, src: proxyNoobClubImage(block.src) }
  }

  return block
}

function isNoobClubDecorativeImageUrl(value = '') {
  let src = String(value || '')

  try {
    const url = new URL(src)
    src = url.searchParams.get('url') || src
  } catch {
    // Keep the original value for pattern matching.
  }

  return /(?:\/media\/icons\/|\/Themes\/|normal_post|topic\/(?:new|normal)|spacer|blank|avatar|smileys?)/i.test(src)
}

function meaningfulArticleBlock(block = {}) {
  if (['image', 'video', 'embed'].includes(block.type)) return Boolean(block.src)
  if (block.type === 'list') return (block.items || []).some((item) => stripHtml(item).trim().length > 12)
  return stripHtml(block.html || block.text || '').trim().length > 12
}

function cleanNoobClubTitle(value = '') {
  return String(value || '')
    .replace(/^Печать страницы\s*-\s*/i, '')
    .replace(/^Тема:\s*/i, '')
    .replace(/\s*\([^)]*\d+[^)]*\)\s*$/, '')
    .trim()
}

function cleanNoobClubAuthor(value = '') {
  const decoded = decodeEscapedArticleHtml(value || '')
  return stripHtml(decoded)
    .replace(/^(?:Автор(?: темы)?|Автор сообщения)\s*:\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function noobClubBlockText(block = {}) {
  const content = block.type === 'list'
    ? (block.items || []).join(' ')
    : block.html || block.text || ''
  return stripHtml(decodeEscapedArticleHtml(content)).replace(/\s+/g, ' ').trim()
}

function promoteNoobClubImageLink(block = {}, articleTitle = '') {
  if (!['html', 'paragraph'].includes(block.type)) return block

  const content = decodeEscapedArticleHtml(block.html || block.text || '')
  const href = content.match(/<a\b[^>]*href=["']([^"']+)["']/i)?.[1]
  const plainText = stripHtml(content).replace(/^\(|\)$/g, '').trim()
  const candidate = [href, plainText].find((value) => /\.(?:avif|gif|jpe?g|png|webp)(?:$|[?#])/i.test(String(value || '')))
  const src = normalizeLink(candidate || '', 'https://www.noob-club.ru')

  return src
    ? { type: 'image', src: proxyNoobClubImage(src), alt: articleTitle || 'Noob Club article image' }
    : block
}

function cleanNoobClubArticleBlocks(blocks = [], articleTitle = '') {
  const sourceBlocks = Array.isArray(blocks) ? blocks : []
  const texts = sourceBlocks.map(noobClubBlockText)
  const titleMetaIndex = texts.findIndex((text) => /^Название\s*:/i.test(text))
  const sentMetaIndex = texts.findIndex((text, index) => index >= Math.max(titleMetaIndex, 0) && /^Отправлено\s*:/i.test(text))
  const startIndex = sentMetaIndex !== -1 ? sentMetaIndex + 1 : titleMetaIndex !== -1 ? titleMetaIndex + 1 : 0
  const nextPostIndex = texts.findIndex((text, index) => index >= startIndex && /(?:^|\s)Название\s*:/i.test(text))
  const endIndex = nextPostIndex > startIndex ? nextPostIndex : sourceBlocks.length
  const contentBlocks = sourceBlocks.slice(startIndex, endIndex)
  const trailingText = nextPostIndex >= startIndex
    ? noobClubBlockText(sourceBlocks[nextPostIndex]).split(/\s+Название\s*:/i)[0].trim()
    : ''

  if (trailingText) contentBlocks.push({ type: 'paragraph', text: trailingText })

  return contentBlocks
    .filter((block) => !isNoobClubPageChrome(block))
    .filter((block) => !/^(?:Печать страницы\s*-|Новости игр Blizzard\s*\/\s*noob-club\.ru|World of Warcraft\s*=>|Название\s*:|Отправлено\s*:)/i.test(noobClubBlockText(block)))
    .map((block) => promoteNoobClubImageLink(block, articleTitle))
    .map((block) => {
      if (block.type === 'paragraph') {
        return { ...block, text: String(block.text || '').replace(/:?cut:?/gi, ' ').replace(/\s+/g, ' ').trim() }
      }
      if (['html', 'quote'].includes(block.type)) {
        return { ...block, html: String(block.html || '').replace(/:?cut:?/gi, ' ') }
      }
      return block
    })
}

function textFromNode(node, selector) {
  return decodeHtml(node.querySelector(selector)?.textContent?.trim() || '')
}

function attrFromNode(node, selector, attr) {
  return node.querySelector(selector)?.getAttribute(attr)?.trim() || ''
}

function normalizeLink(value = '', baseUrl = 'https://www.wowhead.com') {
  const link = decodeHtml(value).trim()
  if (!link) return ''
  try {
    const url = new URL(link, baseUrl)
    if (/(^|\.)noob-club\.ru$/i.test(url.hostname)) {
      url.searchParams.delete('PHPSESSID')
      url.hash = ''
      const topicId = String(url.searchParams.get('topic') || '').match(/^\d+/)?.[0]
      if (topicId && (url.hostname === 'noob-club.ru' || url.hostname === 'www.noob-club.ru')) {
        url.protocol = 'https:'
        url.hostname = 'www.noob-club.ru'
        url.pathname = '/index.php'
        url.search = ''
        url.searchParams.set('topic', `${topicId}.0`)
      }
    }
    return ['http:', 'https:'].includes(url.protocol) ? url.toString() : ''
  } catch {
    return ''
  }
}

function getCategory(item, feed) {
  const category = item.querySelector('category')?.textContent?.trim()
  if (category) return decodeHtml(category)

  const title = textFromNode(item, 'title')
  const match = title.match(/\[(.*?)\]/)
  return match?.[1] || (feed?.source === 'noob-club' ? 'Noob Club' : 'Blue post')
}

function parseDate(value) {
  const timestamp = Date.parse(value || '')
  return Number.isNaN(timestamp) ? null : new Date(timestamp).toISOString()
}

function findImageUrl(item, rawDescription, origin) {
  const rawContent = textFromNode(item, 'content\\:encoded')
  const imagePattern = /https?:\/\/[^\s<>"')\]]+\.(?:avif|gif|jpe?g|png|webp)(?:\?[^\s<>"')\]]*)?/i
  const candidates = [
    attrFromNode(item, 'media\\:content', 'url'),
    attrFromNode(item, 'media\\:thumbnail', 'url'),
    attrFromNode(item, 'enclosure', 'url'),
    rawContent.match(/<img[^>]+src=["']([^"']+)["']/i)?.[1],
    rawDescription.match(/<img[^>]+src=["']([^"']+)["']/i)?.[1],
    rawContent.match(/\[img\]([^\[]+)\[\/img\]/i)?.[1],
    rawDescription.match(/\[img\]([^\[]+)\[\/img\]/i)?.[1],
    rawContent.match(imagePattern)?.[0],
    rawDescription.match(imagePattern)?.[0],
    textFromNode(item, 'image url'),
  ].filter(Boolean)

  const image = candidates
    .map((candidate) => normalizeLink(candidate, origin))
    .find((candidate) => candidate && !/\/Themes\/[^/]+\/images\/(?:topic\/)?(?:normal_post|new|on|off)\.(?:gif|png)$/i.test(candidate))

  return image || ''
}

function proxyNoobClubImage(url = '') {
  try {
    const imageUrl = new URL(url)
    return /(^|\.)noob-club\.ru$/i.test(imageUrl.hostname) ? createProxyUrl(imageUrl.toString()) : imageUrl.toString()
  } catch {
    return ''
  }
}

function parseFeed(xmlText, feed = WOWHEAD_NEWS_FEEDS[0]) {
  const documentXml = new DOMParser().parseFromString(xmlText, 'application/xml')
  const parserError = documentXml.querySelector('parsererror')

  if (parserError) {
    throw new Error('Unable to parse Wowhead RSS feed.')
  }

  return [...documentXml.querySelectorAll('item')].map((item, index) => {
    const rawDescription = textFromNode(item, 'description')
    const summary = cleanFeedSummaryText(stripHtml(rawDescription))
    const title = textFromNode(item, 'title').replace(/^\[[^\]]+\]\s*/, '')

    return {
      id: textFromNode(item, 'guid') || normalizeLink(textFromNode(item, 'link'), feed.origin) || `${feed.key}-post-${index}`,
      title,
      summary,
      category: getCategory(item, feed),
      feedKey: feed.key,
      feedLabel: feed.label,
      kind: feed.kind,
      source: feed.source || 'wowhead',
      sourceLabel: feed.source === 'noob-club' ? 'Noob Club' : 'Wowhead',
      nativeLanguage: feed.nativeLanguage || 'en',
      imageUrl: feed.source === 'noob-club'
        ? proxyNoobClubImage(findImageUrl(item, rawDescription, feed.origin))
        : findImageUrl(item, rawDescription, feed.origin),
      link: normalizeLink(textFromNode(item, 'link'), feed.origin),
      publishedAt: parseDate(textFromNode(item, 'pubDate')),
    }
  })
}

function uniquePosts(posts) {
  const seen = new Set()
  return posts.filter((post) => {
    const contentKey = [
      normalizedFeedTextKey(post.title),
      normalizedFeedTextKey(post.summary),
    ].filter(Boolean).join('|')
    const keys = [
      post.link,
      post.id,
      contentKey ? `content:${contentKey}` : '',
      ...bluePostDedupeKeys(post),
    ].filter(Boolean)

    if (keys.some((key) => seen.has(key))) return false
    keys.forEach((key) => seen.add(key))
    return true
  })
}

function sortPostsByDate(posts) {
  return [...posts].sort((a, b) => {
    return Date.parse(b.publishedAt || '') - Date.parse(a.publishedAt || '')
  })
}

async function requestFeed(url) {
  const response = await fetch(url, {
    headers: {
      Accept: 'application/rss+xml, application/xml, text/xml;q=0.9, */*;q=0.8',
    },
  })

  if (!response.ok) {
    throw new Error(`Wowhead RSS request failed: ${response.status}`)
  }

  return response.text()
}

async function requestJson(url) {
  const response = await fetch(url, {
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`Wowhead worker request failed: ${response.status}`)
  }

  return response.json()
}

export async function getBlueTrackerPosts(lang = 'en') {
  return getWowheadFeedPosts('blue', lang)
}

function getFeedCacheEntry(feedKey = 'blue', lang = 'en') {
  const requestKey = `${feedKey}:${lang}`
  const cached = feedCache.get(requestKey)
  return cached && Date.now() - cached.timestamp < FEED_CACHE_TTL_MS ? cached : null
}

function rememberFeedCache(feedKey, lang, posts) {
  const requestKey = `${feedKey}:${lang}`
  feedCache.set(requestKey, {
    posts,
    timestamp: Date.now(),
    translationProvider: lang === 'en' ? null : getLastTranslationProvider(),
  })

  if (lang === 'en') persistFeedCache(feedKey, posts)
}

function persistFeedCache(feedKey, posts) {
  try {
    localStorage.setItem(`${FEED_STORAGE_PREFIX}${feedKey}`, JSON.stringify({
      timestamp: Date.now(),
      posts,
    }))
  } catch {
    // The in-memory cache remains available when storage is full or disabled.
  }
}

function loadPersistedFeedCache(feedKey) {
  try {
    const cached = JSON.parse(localStorage.getItem(`${FEED_STORAGE_PREFIX}${feedKey}`) || 'null')
    return Array.isArray(cached?.posts) && cached.posts.length
      ? { posts: cached.posts, timestamp: Number(cached.timestamp || 0) }
      : null
  } catch {
    return null
  }
}

export function getFeedFallbackStatus(feedKey = 'blue') {
  return feedFallbacks.get(feedKey) || null
}

function clearFeedCache(feedKey = 'blue', lang = '') {
  [...feedCache.keys()].forEach((key) => {
    if (key === `${feedKey}:${lang}` || (!lang && key.startsWith(`${feedKey}:`))) {
      feedCache.delete(key)
    }
  })
}

export async function getWowheadFeedPosts(feedKey = 'blue', lang = 'en', options = {}) {
  if (options.force) clearFeedCache(feedKey, lang)

  const requestKey = `${feedKey}:${lang}`
  const cached = getFeedCacheEntry(feedKey, lang)

  if (cached) {
    if (lang !== 'en' && cached.translationProvider) {
      setTranslationProvider({
        ...cached.translationProvider,
        cached: true,
      })
    }
    return cached.posts
  }

  if (feedRequests.has(requestKey)) {
    return feedRequests.get(requestKey)
  }

  const request = loadWowheadFeedPosts(feedKey, lang)
    .then((posts) => {
      feedFallbacks.delete(feedKey)
      rememberFeedCache(feedKey, lang, posts)
      return posts
    })
    .catch((error) => {
      const persisted = lang === 'en' ? loadPersistedFeedCache(feedKey) : null
      if (!persisted) throw error

      feedFallbacks.set(feedKey, {
        timestamp: persisted.timestamp,
        reason: error?.message || 'Feed request failed',
      })
      feedCache.set(requestKey, {
        posts: persisted.posts,
        timestamp: Date.now(),
        translationProvider: null,
      })
      return persisted.posts
    })
    .finally(() => {
      feedRequests.delete(requestKey)
    })

  feedRequests.set(requestKey, request)
  return request
}

export async function getWowheadFeedPostsRaw(feedKey = 'blue', options = {}) {
  return getWowheadFeedPosts(feedKey, 'en', options)
}

export async function getTranslatedWowheadFeedPosts(feedKey = 'blue', lang = 'en', options = {}) {
  if (lang === 'en') return getWowheadFeedPostsRaw(feedKey, options)
  if (options.force) clearFeedCache(feedKey, lang)

  const cached = getFeedCacheEntry(feedKey, lang)
  if (cached) {
    if (cached.translationProvider) {
      setTranslationProvider({
        ...cached.translationProvider,
        cached: true,
      })
    }
    return {
      posts: cached.posts,
      fromCache: true,
      translationProvider: cached.translationProvider || null,
    }
  }

  const sourcePosts = Array.isArray(options.sourcePosts)
    ? options.sourcePosts
    : await getWowheadFeedPostsRaw(feedKey, options)
  const posts = await translatePosts(sortPostsByDate(uniquePosts(sourcePosts)), lang)
  rememberFeedCache(feedKey, lang, posts)
  return {
    posts,
    fromCache: false,
    translationProvider: getLastTranslationProvider(),
  }
}

async function loadWowheadFeedPosts(feedKey = 'blue', lang = 'en') {
  const posts = await loadWowheadFeedPostsRaw(feedKey)
  return lang === 'en' ? posts : translatePosts(posts, lang)
}

async function loadWowheadFeedPostsRaw(feedKey = 'blue') {
  const feed = WOWHEAD_NEWS_FEEDS.find((item) => item.key === feedKey) || WOWHEAD_NEWS_FEEDS[0]

  if (feed.source === 'noob-club') {
    const payload = await requestJson(createWorkerUrl('/noob-club-feed', {
      parser: WOWHEAD_ARTICLE_PARSER_VERSION,
    }))
    const posts = Array.isArray(payload.posts) ? payload.posts : []

    if (!posts.length) throw new Error('Noob Club site did not return news posts.')

    return sortPostsByDate(uniquePosts(posts.map((post, index) => ({
      id: normalizeLink(post.id || post.link, feed.origin) || `noob-club-site-${index}`,
      title: cleanNoobClubTitle(post.title || ''),
      summary: cleanFeedSummaryText(post.summary || ''),
      category: post.category || 'Noob Club',
      feedKey: feed.key,
      feedLabel: feed.label,
      kind: feed.kind,
      source: 'noob-club',
      sourceLabel: 'Noob Club',
      nativeLanguage: 'ru',
      imageUrl: proxyNoobClubImage(normalizeLink(post.imageUrl || '', feed.origin)),
      link: normalizeLink(post.link || '', feed.origin),
      publishedAt: parseDate(post.publishedAt),
      author: post.author || '',
    }))))
  }

  if (feed.kind === 'aggregate') {
    const sourceFeeds = feed.sources
      .map((key) => WOWHEAD_NEWS_FEEDS.find((item) => item.key === key))
      .filter(Boolean)
    const results = await Promise.all(sourceFeeds.map(async (sourceFeed) => parseFeed(await requestFeed(createProxyUrl(sourceFeed.url)), sourceFeed)))
    return sortPostsByDate(uniquePosts(results.flat()))
  }

  return sortPostsByDate(uniquePosts(parseFeed(await requestFeed(createProxyUrl(feed.url)), feed)))
}

export async function getWowheadArticleImage(url, source = 'wowhead') {
  const article = await getNewsArticle(url, source)
  return article.imageUrl || ''
}

export async function getNewsArticle(url, source = 'wowhead') {
  const feed = source === 'noob-club' ? WOWHEAD_NEWS_FEEDS.find((item) => item.key === 'noob-club') : null
  const normalizedUrl = normalizeLink(url, feed?.origin || (source === 'noob-club' ? 'https://www.noob-club.ru' : 'https://www.wowhead.com'))
  const article = await requestJson(createWorkerUrl('/article', {
    url: normalizedUrl,
    source,
    parser: WOWHEAD_ARTICLE_PARSER_VERSION,
  }))
  const articleTitle = source === 'noob-club'
    ? cleanNoobClubTitle(article.title || 'Noob Club article')
    : article.title || 'Wowhead article'
  const sourceBlocks = Array.isArray(article.blocks) ? article.blocks : []
  const preparedSourceBlocks = source === 'noob-club'
    ? cleanNoobClubArticleBlocks(sourceBlocks, articleTitle)
    : sourceBlocks
  const parsedBlocks = preparedSourceBlocks.length
    ? preparedSourceBlocks
      .filter((block) => source !== 'noob-club' || !isNoobClubPageChrome(block))
      .map(normalizeArticleBlock)
    : [{ type: 'paragraph', text: article.description || 'Open the source article on Wowhead to read the full post.' }]
  const cleanedBlocks = source === 'noob-club'
    ? parsedBlocks
      .filter((block) => !isNoobClubPageChrome(block))
      .map(normalizeNoobClubArticleBlock)
      .filter((block) => block?.type && (block.type !== 'image' || block.src))
    : parsedBlocks
  const blocks = source === 'noob-club' && !cleanedBlocks.some(meaningfulArticleBlock)
    ? [{ type: 'paragraph', text: article.description || 'Полный текст временно недоступен. Откройте оригинал статьи на Noob Club.' }]
    : cleanedBlocks
  const fallbackImageUrl = blocks.find((block) => block.type === 'image' && block.src)?.src || ''
  const articleImageUrl = source === 'noob-club' && isNoobClubDecorativeImageUrl(article.imageUrl)
    ? ''
    : article.imageUrl || ''

  return {
    title: articleTitle,
    description: article.description || '',
    imageUrl: source === 'noob-club'
      ? proxyNoobClubImage(articleImageUrl || fallbackImageUrl)
      : articleImageUrl || fallbackImageUrl,
    author: source === 'noob-club' ? cleanNoobClubAuthor(article.author) : article.author || '',
    publishedAt: article.publishedAt || '',
    url: article.url || normalizedUrl,
    schemaVersion: Number(article.schemaVersion || 1),
    contentStatus: article.contentStatus || (blocks.some(meaningfulArticleBlock) ? 'full' : 'fallback'),
    blocks,
  }
}

export const getWowheadArticle = getNewsArticle

export { BLUE_TRACKER_RSS_URL, NOOB_CLUB_NEWS_URL, WOWHEAD_NEWS_FEEDS }
